import debounce from 'lodash/debounce';
import * as React from 'react';

import { makeStyles } from '@egr/xbox/utils/tss-react';

type ScrollbarClasses = 'visible' | 'root';

/**
 * -webkit-scrollbar styles only work in FF
 */
export const useScrollbarStyles = makeStyles<ScrollbarProps, ScrollbarClasses>({ name: 'Scrollbar' })((_, { color = 'rgba(0,0,0,.2)', flexBase, direction }, classes) => {
    let overflowProperties: React.CSSProperties;

    switch (direction) {
        case 'horizontal':
            overflowProperties = {
                overflowX: 'auto',
                overflowY: 'hidden',
            };
            break;
        case 'vertical':
            overflowProperties = {
                overflowX: 'hidden',
                overflowY: 'auto',
            };
            break;
        default:
            overflowProperties = { overflow: 'auto' };
            break;
    }

    return {
        root: {
            ...overflowProperties,
            width: '100%',
            position: 'relative',
            flex: flexBase == null ? '1 1 auto' : `1 1 ${flexBase}`,
            transition: '0.3s',
            // only effects webkit browsers e.g. Chrome
            '&::-webkit-scrollbar': {
                width: color === 'transparent' ? 0 : 9,
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
                width: color === 'transparent' ? 0 : 9,
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'transparent',
            },
            [`&.${classes.visible}::-webkit-scrollbar-thumb`]: {
                backgroundColor: color,
            },
            '.wcf-hover-enabled &:hover::-webkit-scrollbar-thumb': {
                backgroundColor: color,
            },
            scrollbarWidth: color === 'transparent' ? 'none' : 'thin',
            // only effects FF
            scrollbarColor: 'transparent transparent',
            [`.wcf-hover-enabled &:hover, &.${classes.visible}`]: {
                scrollbarColor: `${color} transparent`,
            },
        },
        visible: { /* */ },
    };
});

export interface ScrollbarProps extends React.HTMLAttributes<HTMLDivElement>, React.RefAttributes<HTMLDivElement> {
    alwaysVisible?: boolean;
    direction?: 'vertical' | 'horizontal'
    flexBase?: string;
    color?: string;
}

export const Scrollbar: React.FC<ScrollbarProps> =
    React.forwardRef<HTMLDivElement, ScrollbarProps>(
        ({ flexBase = 'auto', onScroll: onScrollProp, className, alwaysVisible, direction, color, ...props}, ref: React.ForwardedRef<HTMLDivElement>) => {
        const { classes, cx } = useScrollbarStyles({ flexBase, direction, color });

        const [visible, setVisible] = React.useState<boolean>(alwaysVisible === true);

        const onScroll: (event: React.UIEvent<HTMLDivElement>) => void = (event) => {
            if (onScrollProp != null) {
                onScrollProp(event);
            }

            if (visible && alwaysVisible) {
                return;
            }

            setVisible(true);

            if (!alwaysVisible) {
                debounce(() => setVisible(false), 500)();
            }
        };

        return (
            <div
                onScroll={onScroll}
                className={cx(
                    className,
                    classes.root,
                    visible ? classes.visible : undefined,
                )}
                {...props}
                ref={ref}
            >
                {props.children}
            </div>
        );
    }
);